import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';
import { getVehicle } from '../api/vehiclesApi';
import { getFullPhotoUrl } from '../utils/imageUrl';

function VehicleDetail() {
  const { loadId, vehicleId } = useParams();
  const navigate = useNavigate();

  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewingPhoto, setViewingPhoto] = useState(null);

  useEffect(() => {
    async function fetchVehicle() {
      try {
        const result = await getVehicle(vehicleId);
        if (result.success) {
          setVehicle(result.vehicle);
        } else {
          setError(result.message || 'Failed to load vehicle.');
        }
      } catch (err) {
        setError('Could not connect to server.');
      } finally {
        setLoading(false);
      }
    }

    fetchVehicle();
  }, [vehicleId]);

  // Determine if an inspection button should show, and for which mode.
  // A vehicle awaiting pickup needs a pickup inspection.
  // A vehicle already picked up (in transit) needs a delivery inspection once it arrives.
  // Once delivered (or canceled), no further inspection is needed.
  const inspectionMode =
    vehicle?.vehicle_status === 'awaiting' ? 'pickup' :
    vehicle?.vehicle_status === 'picked up' ? 'delivery' :
    null;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <AppHeader title="Loading..." showBack={true} fallbackRoute={`/loads/${loadId}`} />
        <LoadingSpinner fullScreen />
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <AppHeader title="Error" showBack={true} fallbackRoute={`/loads/${loadId}`} />
        <div style={{ padding: 16, color: '#dc2626' }}>{error || 'Vehicle not found.'}</div>
      </div>
    );
  }

  const photos = vehicle.car_photos_url || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <AppHeader
        title={`${vehicle.vehicle_name} ${vehicle.vehicle_model}`}
        showBack={true}
        fallbackRoute={`/loads/${loadId}`}
      />

      <div style={{ padding: 16 }}>
        {/* Info card */}
        <div style={styles.card}>
          <div style={styles.topRow}>
            <span style={styles.vehicleTitle}>
              {vehicle.vehicle_name} {vehicle.vehicle_model}
            </span>
            <StatusBadge status={vehicle.vehicle_status} />
          </div>
          <div style={styles.vin}>VIN: {vehicle.vin_number}</div>
          <div style={styles.routeRow}>
            {vehicle.origin} <span style={styles.arrow}>→</span> {vehicle.destination}
          </div>
        </div>

        {/* Start inspection button — only shows if there's a relevant next inspection step */}
        {inspectionMode && (
          <button
            onClick={() =>
              navigate(`/loads/${loadId}/inspection?mode=${inspectionMode}&vehicleId=${vehicleId}`)
            }
            style={styles.startInspectionBtn}
          >
            {inspectionMode === 'pickup' ? 'Start Pickup Inspection' : 'Start Delivery Inspection'}
          </button>
        )}

        {/* Photo gallery */}
        <div style={styles.sectionTitle}>Photos ({photos.length})</div>

        {photos.length === 0 ? (
          <p style={{ color: '#9ca3af' }}>No photos uploaded yet.</p>
        ) : (
          <div style={styles.photoGrid}>
            {photos.map((photo, index) => {
              const fullUrl = getFullPhotoUrl(photo);
              return (
                <img
                  key={index}
                  src={fullUrl}
                  alt={`Vehicle photo ${index + 1}`}
                  style={styles.photoThumb}
                  onClick={() => setViewingPhoto(fullUrl)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Full-size photo viewer */}
      {viewingPhoto && (
        <div onClick={() => setViewingPhoto(null)} style={styles.photoViewerOverlay}>
          <img src={viewingPhoto} alt="Vehicle full size" style={styles.photoViewerImage} />
        </div>
      )}
    </div>
  );
}

const styles = {
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  vehicleTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111827',
  },
  vin: {
    fontSize: 13,
    color: '#6b7280',
    marginBottom: 8,
  },
  routeRow: {
    fontSize: 13,
    color: '#374151',
    fontWeight: 600,
    borderTop: '1px solid #f3f4f6',
    paddingTop: 10,
  },
  arrow: {
    margin: '0 6px',
    color: '#9ca3af',
  },
  startInspectionBtn: {
    width: '100%',
    backgroundColor: '#2563eb',
    color: 'white',
    fontWeight: 700,
    fontSize: 15,
    padding: '14px 0',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#374151',
    marginBottom: 10,
  },
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 10,
  },
  photoThumb: {
    width: '100%',
    aspectRatio: '1',
    borderRadius: 10,
    objectFit: 'cover',
    cursor: 'pointer',
  },
  photoViewerOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: 20,
  },
  photoViewerImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    borderRadius: 8,
    objectFit: 'contain',
  },
};

export default VehicleDetail;